import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
/* eslint-disable no-param-reassign */
import BookOpen from '@crehana/compass.icons.outline/BookOpen';
import Cube from '@crehana/compass.icons.outline/Cube';
import Link from '@crehana/compass.icons.outline/Link';
import Ranking from '@crehana/compass.icons.outline/Ranking';
import ShoppingCart from '@crehana/compass.icons.outline/ShoppingCart';
import SkillXp from '@crehana/compass.icons.outline/SkillXp';
import { HelperTooltip } from "../../SidebarMenu/components";
import useConfiguration from "../useConfiguration";
import useHigherEducation from "../useHigherEducation";
import useAdminTranslations from "./useAdminTranslations";

/**
 * ELearning codes:
 * ML02: Gammification
 *    ML021: Progreso
 *    ML022: Recompensas
 * ML03: Contenido
 *    ML031: Cursos
 *    ML032: Rutas de aprendizaje
 *    ML033: Proyectos
 * ML04: Skill XP
 * ML01: Integraciones
 */
import { jsx as _jsx } from "react/jsx-runtime";
export var useELearningModules = function useELearningModules(slug) {
  var _useConfiguration = useConfiguration({
      organizationSlug: slug
    }),
    canUseGamification = _useConfiguration.canUseGamification,
    canUseElevate = _useConfiguration.canUseElevate,
    organizationId = _useConfiguration.organizationId,
    canUseSkillXP = _useConfiguration.canUseSkillXP;
  var _useHigherEducation = useHigherEducation({
      organizationId: organizationId
    }),
    canUseShop = _useHigherEducation.isHigherEducationActive;
  var _useAdminTranslations = useAdminTranslations(),
    t = _useAdminTranslations.t;
  var getLabel = function getLabel(code) {
    var labels = {
      ML01: t('LEARNING_LINK_INTEGRATIONS'),
      ML02: t('LEARNING_LINK_GAMIFICATION'),
      ML021: t('LEARNING_LINK_PROGRESS'),
      ML022: t('LEARNING_LINK_REWARDS'),
      ML03: t('LEARNING_LINK_CONTENT'),
      ML031: t('LEARNING_LINK_COURSES'),
      ML032: t('LEARNING_LINK_PATHS'),
      ML033: t('LEARNING_LINK_PROJECTS'),
      ML04: t('LEARNING_LINK_SKILL_XP_EVOLUTION')
    };
    return (labels === null || labels === void 0 ? void 0 : labels[code]) || '';
  };
  var getIcon = function getIcon(code) {
    var icons = {
      ML01: Cube,
      ML02: Ranking,
      ML03: BookOpen,
      ML04: SkillXp
    };

    // Default Link icons if code doesn't exists.
    return (icons === null || icons === void 0 ? void 0 : icons[code]) || Link;
  };
  var getTooltip = function getTooltip(code) {
    var tooltips = {
      ML01: {
        title: t('LEARNING_LINK_INTEGRATIONS'),
        description: t('LEARNING_LINK_INTEGRATIONS_TOOLTIP_DESCRIPTION')
      },
      ML02: {
        title: t('LEARNING_LINK_GAMIFICATION'),
        description: t('LEARNING_LINK_GAMIFICATION_TOOLTIP_DESCRIPTION')
      },
      ML03: {
        title: t('LEARNING_LINK_CONTENT'),
        description: t('LEARNING_LINK_CONTENT_TOOLTIP_DESCRIPTION')
      },
      ML04: {
        title: t('LEARNING_LINK_SKILL_XP_EVOLUTION'),
        description: t('LEARNING_LINK_SKILL_XP_EVOLUTION_TOOLTIP_DESCRIPTION')
      }
    };
    return (tooltips === null || tooltips === void 0 ? void 0 : tooltips[code]) || '';
  };
  var getUrl = function getUrl(code) {
    var urls = {
      ML01: "/org/".concat(slug, "/panel/integraciones/"),
      ML02: "/org/".concat(slug, "/panel/gamification"),
      ML021: "/org/".concat(slug, "/panel/gamification/progress"),
      ML022: "/org/".concat(slug, "/panel/gamification/rewards"),
      ML03: "/org/".concat(slug, "/panel/content/"),
      ML031: "/org/".concat(slug, "/panel/content/courses/"),
      ML032: "/org/".concat(slug, "/panel/content/paths/"),
      ML033: "/org/".concat(slug, "/panel/content/projects/"),
      ML04: "/org/".concat(slug, "/panel/skill-xp-evolution/")
    };
    return (urls === null || urls === void 0 ? void 0 : urls[code]) || '';
  };
  var mappedNestedModules = function mappedNestedModules(modules) {
    return (modules === null || modules === void 0 ? void 0 : modules.map(function (e) {
      var label = getLabel(e.code);
      return {
        code: e.code,
        label: e.label || label,
        url: getUrl(e.code)
      };
    })) || [];
  };
  var mappedModules = function mappedModules(modules) {
    return (modules === null || modules === void 0 ? void 0 : modules.map(function (e) {
      var Icon = getIcon(e.code);
      var label = getLabel(e.code);
      var tooltip = getTooltip(e.code);
      return {
        code: e.code,
        label: e.label || label,
        icon: Icon,
        tooltip: /*#__PURE__*/_jsx(HelperTooltip, {
          title: tooltip.title,
          description: tooltip.description
        }),
        url: getUrl(e.code),
        nested: mappedNestedModules(e.submodules) || []
      };
    })) || [];
  };

  /**
   * IMPORTANT: Backend needs to confirm pending modules:
   * SHOPPING and INTEGRATIONS.
   * Also, needs to confirm if the feature flags are not more necessary
   * validating from fronted.
   */
  var getELearningLinks = function getELearningLinks(modules) {
    var backendModules = mappedModules(modules || []);
    return [].concat(_toConsumableArray(backendModules), [{
      code: 'SHOPPING',
      label: t('LEARNING_LINK_SHOP'),
      icon: ShoppingCart,
      url: "/org/".concat(slug, "/panel/mi-tienda"),
      nested: [],
      tooltip: /*#__PURE__*/_jsx(HelperTooltip, {
        title: t('LEARNING_LINK_SHOP'),
        description: t('LEARNING_LINK_SHOP_TOOLTIP_DESCRIPTION')
      })
    }])
    /*
     * @returns The items menu for ELearning product.
     * Validating the Feature Flags for each item if have it.
     * Some links has a Lock icon, other only filtered from menu.
     */
    .filter(function (link) {
      return !(link.code === 'SHOPPING' && !canUseShop);
    }).reduce(function (acc, link) {
      if (link.nested) {
        link.nested = link.nested.map(function (s) {
          var isLocked = s.code === 'ML031' && !canUseElevate;
          return _objectSpread(_objectSpread({}, s), {}, {
            isLocked: isLocked
          });
        });
      }
      var disableGamification = link.code === 'ML02' && !canUseGamification;
      var disableSkillXP = link.code === 'ML04' && !canUseSkillXP;
      if (disableGamification || disableSkillXP) {
        // biome-ignore lint/style/noParameterAssign: parameter reassignment is intentional
        link = _objectSpread(_objectSpread({}, link), {}, {
          isLocked: true
        });
      }
      acc.push(link);
      return acc;
    }, []);
  };
  return {
    getELearningLinks: getELearningLinks
  };
};